/* eslint-disable no-console */

import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
	public state: State = {
		hasError: false,
		error: null,
		errorInfo: null,
	};

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error, errorInfo: null };
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('Uncaught error:', error, errorInfo);
		this.setState({
			error,
			errorInfo,
		});

		// In development, you might want to send this to a logging service
		if (process.env.NODE_ENV === 'development') {
			console.group('🔥 Error Details (Development Only)');
			console.log('Error:', error);
			console.log('Component Stack:', errorInfo.componentStack);
			console.groupEnd();
		}
	}

	public render() {
		if (this.state.hasError) {
			if (process.env.NODE_ENV === 'development') {
				return (
					<div className="p-4 bg-red-50 border-l-4 border-red-500">
						<h1 className="text-xl font-bold text-red-700">Something went wrong</h1>
						<div className="mt-4">
							<p className="font-mono text-sm text-red-600">
								{this.state.error?.toString()}
							</p>
							<pre className="mt-2 text-xs bg-red-100 p-4 rounded overflow-auto">
								{this.state.errorInfo?.componentStack}
							</pre>
						</div>
					</div>
				);
			}

			// Production error UI
			return (
				<div className="p-4 text-center">
					<h1 className="text-xl font-bold">Oops! Something went wrong</h1>
					<p className="mt-2">Please try refreshing the page</p>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
